import appStore from '../utils/state';
import gsap from 'gsap';

export default class CartToast extends HTMLElement {
  private panel: HTMLElement | null = null;
  private status: HTMLElement | null = null;
  private button: HTMLButtonElement | null = null;
  private check: SVGPathElement | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private unsubscribe: (() => void) | null = null;
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  connectedCallback(): void {
    this.panel = this.querySelector('[data-cart-toast-ref="panel"]');
    this.status = this.querySelector('[data-cart-toast-ref="status"]');
    this.button = this.querySelector('[data-cart-toast-ref="button"]');
    this.check = this.querySelector('[data-cart-toast-ref="check"]');
    this.button?.addEventListener('click', this.onClick);
    this.button?.addEventListener('focus', this.onFocus);
    this.button?.addEventListener('blur', this.onBlur);
    this.unsubscribe = appStore.subscribeKey('isCartModalOpen', (isOpen) => {
      if (isOpen) this.hide();
    });
  }

  show(): void {
    if (!this.panel) return;
    clearTimeout(this.timer);
    this.panel.inert = false;
    this.hideTl?.kill();
    this.showTl?.kill();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.showTl = gsap.timeline();
    this.showTl.to(this.panel, { autoAlpha: 1, duration: reducedMotion ? 0 : 0.2, ease: 'power2.out' }, 0);
    this.showTl.fromTo(
      this.panel,
      { y: reducedMotion ? 0 : 12 },
      { y: 0, duration: reducedMotion ? 0 : 0.6, ease: 'power3.out' },
      0,
    );
    if (this.check) {
      this.showTl.fromTo(
        this.check,
        { attr: { 'stroke-dasharray': 1, 'stroke-dashoffset': 1 } },
        { attr: { 'stroke-dashoffset': 0 }, duration: reducedMotion ? 0 : 0.6, ease: 'power2.out' },
        reducedMotion ? 0 : 0.3,
      );
    }
    if (this.status) this.status.textContent = this.dataset.message ?? '';
    if (document.activeElement !== this.button) this.timer = setTimeout(() => this.hide(), 3000);
  }

  private hide(): void {
    clearTimeout(this.timer);
    if (this.panel) {
      this.panel.inert = true;
      this.showTl?.kill();
      this.hideTl?.kill();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.hideTl = gsap.timeline();
      this.hideTl.to(this.panel, { autoAlpha: 0, duration: reducedMotion ? 0 : 0.2, ease: 'power2.out' }, 0);
      this.hideTl.to(
        this.panel,
        { y: reducedMotion ? 0 : -12, duration: reducedMotion ? 0 : 0.6, ease: 'power3.out' },
        0,
      );
    }
    if (this.status) this.status.textContent = '';
  }

  private onClick = (): void => {
    appStore.actions.openCartModal();
  };

  private onFocus = (): void => {
    clearTimeout(this.timer);
  };

  private onBlur = (): void => {
    if (this.panel && !this.panel.inert) this.timer = setTimeout(() => this.hide(), 3000);
  };

  disconnectedCallback(): void {
    this.hide();
    this.showTl?.kill();
    this.hideTl?.kill();
    this.showTl = null;
    this.hideTl = null;
    this.button?.removeEventListener('click', this.onClick);
    this.button?.removeEventListener('focus', this.onFocus);
    this.button?.removeEventListener('blur', this.onBlur);
    this.unsubscribe?.();
  }
}

customElements.define('cart-toast', CartToast);
